// client/src/v2/telemetry.ts
//
// Telemetria leve pro tester — wrapper unico pra logs do client.
// Diferente do rclog() especifico de revenuecat, esse e generico e usado
// em todo o app. Caracteristicas:
//   - Session ID estavel por boot (UUID truncado) — correlaciona logs
//   - Buffer + batch (5s) — junta logs em 1 POST, economiza bateria/rede
//   - Captura global de window.onerror + unhandledrejection
//   - Mantem ultimos N logs em RAM pra a pagina de Diagnostico mostrar
//   - keepalive: true sobrevive a navegacao
//
// LGPD: nunca passe email/senha/token completo. Use maskEmail/maskId helpers.

type LogEntry = {
  ts: number;
  tag: string;
  data?: any;
};

const MAX_RECENT = 200;
const BATCH_INTERVAL = 5000;
const MAX_BATCH = 50;

const SESSION_ID: string = (() => {
  try {
    const arr = new Uint8Array(8);
    (window.crypto || (window as any).msCrypto).getRandomValues(arr);
    return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return Math.random().toString(36).slice(2, 18);
  }
})();

const recent: LogEntry[] = [];
let buffer: LogEntry[] = [];
let flushTimer: any = null;
let currentUserId: string | null = null;

export function setTelemetryUser(userId: string | null): void {
  currentUserId = userId;
}

function flush() {
  if (!buffer.length) return;
  const batch = buffer.splice(0, MAX_BATCH);
  try {
    fetch('/api/debug-log/batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session: SESSION_ID,
        userId: currentUserId,
        entries: batch,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {}
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, BATCH_INTERVAL);
}

export function tlog(tag: string, data?: any): void {
  const entry: LogEntry = { ts: Date.now(), tag: String(tag).slice(0, 80), data: data ?? null };
  recent.push(entry);
  if (recent.length > MAX_RECENT) recent.shift();
  buffer.push(entry);
  scheduleFlush();
  try { console.log('[tlog]', tag, data); } catch {}
}

export function getRecentLogs(): LogEntry[] {
  return [...recent];
}

export function getSessionId(): string {
  return SESSION_ID;
}

export function flushNow(): void {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  flush();
}

export function maskEmail(e: string | null | undefined): string {
  if (!e) return '';
  const at = e.indexOf('@');
  if (at < 1) return '***';
  return e[0] + '***' + e.slice(at);
}

export function maskId(id: string | null | undefined): string {
  if (!id) return '';
  if (id.length <= 8) return id;
  return id.slice(0, 8) + '...';
}

let installed = false;
export function installGlobalErrorCapture(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (e: ErrorEvent) => {
    try {
      tlog('error:uncaught', {
        message: e.message,
        src: e.filename,
        line: e.lineno,
        col: e.colno,
        stack: String(e.error?.stack || '').slice(0, 1500),
      });
    } catch {}
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    try {
      const reason: any = e.reason;
      tlog('error:promise', {
        message: reason?.message || String(reason).slice(0, 500),
        stack: String(reason?.stack || '').slice(0, 1500),
      });
    } catch {}
  });

  window.addEventListener('pagehide', () => flushNow());
  window.addEventListener('beforeunload', () => flushNow());
}
